import { useContext, useEffect, useId, useState } from 'react';
import { ChatContext } from '@/contexts/ChatContext';
import { ELVIS_SKRIVER } from '@/lib/elvisSignal';

export type ElvisLage = 'vila' | 'lyssnar' | 'tanker' | 'skriver' | 'glad' | 'fundersam' | 'fel';

const LAGEN: ElvisLage[] = ['vila', 'lyssnar', 'tanker', 'skriver', 'glad', 'fundersam', 'fel'];

/** Hur länge ett efterläge står kvar innan ELvis går tillbaka till vila (ms). */
const EFTERLAGE_MS: Record<'glad' | 'fundersam' | 'fel', number> = { glad: 2600, fundersam: 4200, fel: 4200 };
/** Så länge efter senaste tangenttryckningen han fortsätter lyssna (ms). */
const LYSSNAR_MS = 1300;

/** Bara lokalt: ?maskot=demo låter ett klick bläddra mellan lägena. */
const DEMO =
  import.meta.env.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('maskot') === 'demo';

/**
 * Läget följer chatten:
 * - lyssnar medan någon skriver i frågefältet
 * - tänker från att frågan skickats tills första ordet kommer (sökningen i
 *   kunskapsbasen) – sista meddelandet är då användarens
 * - skriver medan svaret strömmar in – sista meddelandet är hans eget
 * - glad när svaret är klart och grundat i källor, fundersam när det inte är
 *   det (grounded: false – samma sak som blir en kunskapslucka), bekymrad vid fel
 *
 * Övergången när strömningen slutar upptäcks under renderingen, inte i en effect.
 */
function useElvisLage(): [ElvisLage, () => void] {
  const chat = useContext(ChatContext);
  const streaming = chat?.streaming ?? false;
  const fel = chat?.error ?? null;
  const antal = chat?.messages.length ?? 0;
  const sista = chat?.messages[antal - 1];

  const [forraStreaming, setForraStreaming] = useState(streaming);
  // Antal meddelanden när strömningen började. Reaktionen efteråt kräver att
  // ett nytt svar faktiskt kom – annars blev ELvis "glad" när chatten
  // nollställdes mitt i en ström (t.ex. vid byte av inloggad användare).
  const [antalVidStart, setAntalVidStart] = useState(antal);
  const [efter, setEfter] = useState<'glad' | 'fundersam' | 'fel' | null>(null);
  const [lyssnar, setLyssnar] = useState(false);
  const [demoIndex, setDemoIndex] = useState<number | null>(null);

  if (forraStreaming !== streaming) {
    setForraStreaming(streaming);
    if (streaming) {
      setEfter(null);
      setAntalVidStart(antal);
    } else if (fel) {
      setEfter('fel');
    } else if (antal > antalVidStart && sista?.role === 'assistant') {
      setEfter(sista.grounded === false ? 'fundersam' : 'glad');
    }
  }

  useEffect(() => {
    if (!efter) return;
    const t = window.setTimeout(() => setEfter(null), EFTERLAGE_MS[efter]);
    return () => window.clearTimeout(t);
  }, [efter]);

  useEffect(() => {
    let t = 0;
    const pa = () => {
      setLyssnar(true);
      window.clearTimeout(t);
      t = window.setTimeout(() => setLyssnar(false), LYSSNAR_MS);
    };
    window.addEventListener(ELVIS_SKRIVER, pa);
    return () => {
      window.removeEventListener(ELVIS_SKRIVER, pa);
      window.clearTimeout(t);
    };
  }, []);

  const bladdra = () => setDemoIndex((i) => ((i ?? 0) + 1) % LAGEN.length);

  if (DEMO && demoIndex !== null) return [LAGEN[demoIndex], bladdra];
  const lage: ElvisLage = streaming
    ? sista?.role === 'assistant'
      ? 'skriver'
      : 'tanker'
    : efter ?? (lyssnar ? 'lyssnar' : 'vila');
  return [lage, bladdra];
}

const ETIKETT: Record<ElvisLage, string> = {
  vila: 'ELvis',
  lyssnar: 'ELvis lyssnar',
  tanker: 'ELvis letar i kunskapsbasen',
  skriver: 'ELvis skriver sitt svar',
  glad: 'ELvis är klar med svaret',
  fundersam: 'ELvis hittade inget säkert stöd i källorna',
  fel: 'ELvis stötte på ett problem',
};

/**
 * ELvis i lager: kroppen (utan ögon) och antennen är bilder, ögonen ritas som
 * vektor så att de kan blinka, titta och byta form. Måtten är bildens egna
 * pixlar (349 × 535), mätta ur originalet.
 */
export function MaskotElvis({ lage: styrtLage, blick }: {
  /** Tvingat läge – för ytor utan chatt, t.ex. inloggningen. */
  lage?: ElvisLage;
  /** Blickens förskjutning i bildens pixlar; läggs utanpå lägets egen. */
  blick?: { x: number; y: number };
} = {}) {
  const [chattLage, bladdra] = useElvisLage();
  const lage = styrtLage ?? chattLage;
  // Unikt per instans – ett fast id hade krockat om ELvis någonsin visas två gånger
  const glod = `elvis-glod-${useId().replace(/:/g, '')}`;

  return (
    <svg
      viewBox="0 0 349 535"
      className="elvis-maskot block w-full h-auto"
      data-lage={lage}
      onClick={DEMO ? bladdra : undefined}
      role="img"
      aria-label={ETIKETT[lage]}
    >
      <defs>
        <filter id={glod} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      <image href="/maskot/elvis-bas.webp" width="349" height="535" />

      {/* Antennen gungar runt sin fot */}
      <g className="elvis-antenn">
        <circle className="elvis-antenn-sken" cx="83" cy="18" r="34" fill="#2CE6FF" filter={`url(#${glod})`} />
        <image href="/maskot/elvis-antenn.webp" x="58" y="0" width="51" height="58" />
      </g>

      <g transform={blick ? `translate(${blick.x} ${blick.y})` : undefined}>
        <g className="elvis-ogon">
          {[
            { cx: 159.5, cy: 186, rx: 17.5, ry: 25 },
            { cx: 259, cy: 169.5, rx: 15, ry: 24.5 },
          ].map((o, i) => (
            <g key={i} className={`elvis-oga elvis-oga-${i === 0 ? 'v' : 'h'}`}>
              <g className="elvis-oga-oppen">
                <ellipse cx={o.cx} cy={o.cy} rx={o.rx + 3} ry={o.ry + 3} fill="#2CE6FF" opacity="0.55" filter={`url(#${glod})`} />
                <ellipse cx={o.cx} cy={o.cy} rx={o.rx} ry={o.ry} fill="#3BE7FF" />
                <ellipse cx={o.cx - o.rx * 0.3} cy={o.cy - o.ry * 0.35} rx={o.rx * 0.28} ry={o.ry * 0.2} fill="#E6FDFF" opacity="0.7" />
              </g>
              {/* Glada ögon: bågar som ^ ^ */}
              <path
                className="elvis-oga-glad"
                d={`M ${o.cx - o.rx} ${o.cy + 4} Q ${o.cx} ${o.cy - o.ry * 0.9} ${o.cx + o.rx} ${o.cy + 4}`}
                fill="none"
                stroke="#3BE7FF"
                strokeWidth="8"
                strokeLinecap="round"
                filter={`url(#${glod})`}
                opacity="0.6"
              />
              <path
                className="elvis-oga-glad"
                d={`M ${o.cx - o.rx} ${o.cy + 4} Q ${o.cx} ${o.cy - o.ry * 0.9} ${o.cx + o.rx} ${o.cy + 4}`}
                fill="none"
                stroke="#3BE7FF"
                strokeWidth="7"
                strokeLinecap="round"
              />
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
}
